import { CURRENCIES } from "config";
import { formatNumberWithCommas } from "utils";
import {
  getBudgetCategoryActual,
  getBudgetCategoryEstimate,
  getBudgetCategoryVariance,
  getBudgetContingencyVariance,
  getBudgetEstimateContingency,
  getBudgetItemActual,
  getBudgetItemEstimate,
  getBudgetItemVariance,
  getBudgetItemsActual,
  getBudgetItemsEstimate,
  getBudgetItemsVariance,
  getBudgetTotalActual,
  getBudgetTotalEstimate,
  getBudgetTotalVariance,
  getCategoryFringesInBudget,
} from "./BudgetItemUtils";
import { BudgetCategory, BudgetContingency } from "./types";

/** One CSV row: account number, description, estimate, actual, variance. */
export type BudgetCsvRow = [string, string, string, string, string];

const money = (value: number) => formatNumberWithCommas(value, true);

/**
 * The budget CSV: lines, then each account, then each category's fringe row and total, then
 * contingency and the grand total. Category rows plus contingency add up to the grand total.
 */
export function buildBudgetCsvRows(
  sheetData: BudgetCategory[],
  budgetContingency: BudgetContingency | null,
  currency: string
): BudgetCsvRow[] {
  const symbol =
    CURRENCIES.find((cur) => cur.abbreviation === currency.toUpperCase())?.symbol ?? "$";
  const rows: BudgetCsvRow[] = [
    ["Acc #", "Description", `${symbol} Estimate`, `${symbol} Actual`, `${symbol} Variance`],
  ];

  sheetData.forEach((category) => {
    category.sub_categories.forEach((subCategory) => {
      subCategory.items.forEach((item) => {
        item.children.forEach((child) => {
          rows.push([
            child.account_number || "",
            child.name || "",
            money(getBudgetItemEstimate(child)),
            money(getBudgetItemActual(child)),
            money(getBudgetItemVariance(child)),
          ]);
        });
        rows.push([
          item.account_number || "",
          item.name || "",
          money(getBudgetItemEstimate(item)),
          money(getBudgetItemActual(item)),
          money(getBudgetItemVariance(item)),
        ]);
      });
      rows.push([
        subCategory.account_number || "",
        subCategory.name || "",
        money(getBudgetItemsEstimate(subCategory.items)),
        money(getBudgetItemsActual(subCategory.items)),
        money(getBudgetItemsVariance(subCategory.items)),
      ]);
    });

    // Like the top sheet: a fringe row with the category's budget-wide share, then the
    // category total including it.
    const fringes = getCategoryFringesInBudget(category, sheetData);
    if (fringes.nested_fringes.length > 0) {
      rows.push([
        category.fringe_account_number || "",
        `${category.name} FRINGES`,
        money(fringes.estimate),
        money(fringes.actual_amount),
        money(fringes.variance),
      ]);
    }
    rows.push([
      "",
      category.name || "",
      money(getBudgetCategoryEstimate(category, sheetData)),
      money(getBudgetCategoryActual(category, sheetData)),
      money(getBudgetCategoryVariance(category, sheetData)),
    ]);
  });

  rows.push([
    "",
    "CONTINGENCY",
    money(getBudgetEstimateContingency(sheetData, budgetContingency)),
    money(budgetContingency?.actual_amount ?? 0),
    money(getBudgetContingencyVariance(sheetData, budgetContingency)),
  ]);
  rows.push([
    "",
    "GRAND TOTAL",
    money(getBudgetTotalEstimate(sheetData, budgetContingency)),
    money(getBudgetTotalActual(sheetData, budgetContingency)),
    money(getBudgetTotalVariance(sheetData, budgetContingency)),
  ]);

  return rows;
}
